import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { PanelLeft, SquarePen } from 'lucide-react';
import { RecentChats, useNewChat } from '@/features/assistant';
import { AccountMenu } from './AccountMenu';
import { Brand } from './Brand';
import { NavList } from './NavList';
import { useSidebar } from './sidebarStore';

/** Shared by the permanent desktop drawer and the temporary mobile one, which closes itself through `onNavigate`. */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const newChat = useNewChat();
  const collapse = useSidebar().toggle;

  return (
    <Stack sx={{ height: '100%', bgcolor: 'surface.sidebar' }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 1.5, pt: 1.75, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Brand onNavigate={onNavigate} />
        </Box>
        {onNavigate ? null : (
          <Tooltip title="Collapse sidebar">
            <IconButton size="small" aria-label="Collapse sidebar" onClick={collapse} sx={{ color: 'text.secondary' }}>
              <PanelLeft size={17} strokeWidth={1.75} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ px: 1.5, pb: 2 }}>
        <Button fullWidth variant="outlined" startIcon={<SquarePen size={15} strokeWidth={1.75} />} onClick={() => { newChat(); onNavigate?.(); }} sx={newChatSx}>
          New chat
        </Button>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pt: 0.75 }}>
        <NavList onNavigate={onNavigate} />
        <RecentChats onPick={onNavigate} />
      </Box>
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <AccountMenu />
      </Box>
    </Stack>
  );
}

const newChatSx = {
  justifyContent: 'flex-start',
  minHeight: 36,
  color: 'text.primary',
  fontWeight: 500,
  bgcolor: 'background.paper',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(26, 26, 25, 0.04)',
  '&:hover': { bgcolor: 'background.paper', borderColor: 'surface.border' },
} as const;
